// src/pages/Details.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PlateSelector from "./PlateSelector";
import ResponsiveGateControl from "./ResponsiveGateControl";
import UsageRing from "../components/UsageRing";
import { useTranslation } from "react-i18next";
import { useSite } from "../context/SiteContext";
import { usePlateSyncStatus } from "../hooks/usePlateSyncStatus";
import { useDeletedStillActive } from "../hooks/useDeletedStillActive";

function Details() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedSite, areas, setAreas, platesByArea, selectedArea, areaId } = useSite();
  const canSeePlates = selectedSite?.user_role !== "gate_opener";
  const syncStatus = usePlateSyncStatus(areaId, canSeePlates); // null = not loaded yet
  const syncStatusByPlate = syncStatus || {};
  const [syncFilterActive, setSyncFilterActive] = useState(false);
  const deletedStillActive = useDeletedStillActive(areaId, canSeePlates); // null = not loaded yet

  const unsyncedCount = Object.values(syncStatusByPlate)
    .filter((entries) => entries.some((e) => e.display === "warning")).length;

  // keep the dashboard tile's warning in step with what we just learned (no extra request)
  useEffect(() => {
    if (syncStatus === null) return;
    setAreas((prev) => prev.map((a) =>
      a.id === areaId && a.unsynced_plates !== unsyncedCount ? { ...a, unsynced_plates: unsyncedCount } : a
    ));
  }, [syncStatus, unsyncedCount, areaId, setAreas]);

  useEffect(() => {
    if (deletedStillActive === null) return;
    const n = deletedStillActive.length;
    setAreas((prev) => prev.map((a) =>
      a.id === areaId && a.deleted_still_active !== n ? { ...a, deleted_still_active: n } : a
    ));
  }, [deletedStillActive, areaId, setAreas]);

  // filter has nothing left to show once everything is synced (or on area switch) — drop it
  useEffect(() => {
    if (unsyncedCount === 0) setSyncFilterActive(false);
  }, [unsyncedCount, areaId]);

  if (!selectedSite || !selectedArea) {
    return <p>{t("No area selected")}</p>;
  }

  const area = selectedArea;

  const plates = platesByArea[selectedArea.id] || [];

  return (
    <div className="container mt-4">
      <div className="mb-4 d-flex align-items-center gap-3">
        <button className="btn btn-sm btn-light flex-shrink-0" onClick={() => navigate('/dashboard')}>
          <i className="bi bi-arrow-left"></i>
        </button>
        <h3 className="mb-0 text-truncate" style={{ minWidth: 0, flexShrink: 3 }}>{selectedSite.name}</h3>
        <span className="text-muted flex-shrink-0">|</span>
        <i className={`bi ${area.icon || 'bi-p-circle'} text-ez-orange flex-shrink-0`}></i>
        <h5 className="mb-0 text-truncate" style={{ minWidth: 0 }}>{area.name}</h5>
      </div>

      <div className="d-flex flex-column flex-lg-row gap-3">
        {/* Left: Plate Selector */}
        {selectedSite?.user_role !== "gate_opener" && (
          <div id="plate-list-box" className="flex-grow-1">
            <PlateSelector
              syncStatusByPlate={syncStatusByPlate}
              syncFilterActive={syncFilterActive}
              onToggleSyncFilter={() => setSyncFilterActive((v) => !v)}
            />
          </div>
        )}

        {/* Right: Info Panel (desktop only). Width = gate tile (180px) + p-2 box padding, fixed so long text wraps instead of widening the column */}
        <div className="d-none d-lg-flex flex-column align-items-center flex-shrink-0 mt-lg-5" style={{ width: "calc(180px + 1rem)" }}>
          {selectedSite?.user_role !== "gate_opener" && (
            <div id="area-usage-box" className="mb-3 p-2 bg-white rounded-3 shadow-sm w-100 text-center">
              <UsageRing
                value={plates.length}
                total={area.total_spaces ?? 100}
                label={t("Used")}
              />
              {deletedStillActive?.length > 0 && (
                <div id="area-deleted-still-active" className="mt-2 p-2 rounded-3 bg-danger-subtle text-danger-emphasis small text-start">
                  <div className="fw-semibold mb-1">
                    <i className="bi bi-exclamation-octagon-fill me-1"></i>
                    {t("deleted_still_active_title", { count: deletedStillActive.length })}
                  </div>
                  <ul className="mb-1 ps-3">
                    {deletedStillActive.map((p) => (
                      <li key={p.id}>
                        <strong>{p.plate}</strong>
                        {p.gates.length > 0 && <> — {p.gates.map((g) => g.gate_name).join(", ")}</>}
                      </li>
                    ))}
                  </ul>
                  <div>{t("deleted_still_active_hint")}</div>
                </div>
              )}
              {unsyncedCount > 0 && (
                <button
                  id="area-sync-warning"
                  type="button"
                  className={`btn btn-sm w-100 mt-2 border-0 small text-warning-emphasis ${syncFilterActive ? "bg-warning-subtle" : ""}`}
                  onClick={() => setSyncFilterActive((v) => !v)}
                  aria-pressed={syncFilterActive}
                  title={t("sync_filter_hint")}
                >
                  <i className="bi bi-exclamation-triangle-fill text-warning me-1"></i>
                  {t("sync_not_synced", { count: unsyncedCount })}
                </button>
              )}
            </div>
          )}

          <div id="gate-open-box" className="p-2 bg-white rounded-3 shadow-sm w-100 text-center">
            <ResponsiveGateControl embedded={true} area={area} />
          </div>
        </div>
      </div>

      {/* Floating gate button — mobile only */}
      <div id="gate-open-fab" className="d-lg-none">
        <ResponsiveGateControl area={area} />
      </div>
    </div>
  );
}

export default Details;
